import { IconButton, MenuItem, MenuList, Stack, Typography } from '@mui/material';
import {
  DATE_FORMAT_RANGE_FNS,
  useGetAttendanceCodesQuery,
  useGetAttendanceRegisterStatisticsQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { CrossIcon, EditIcon, Loading, ModalHeader } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import React, { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { AccessDenied } from '@schooly/style';
import { CollapsiblePanel } from '../../../components/common/CollapsiblePanel';
import {
  ModalLarge,
  ModalMain,
  ModalSideBar,
} from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeaderV2 } from '../../../components/uikit-components/Modal/ModalHeaderV2';
import { useRouter } from '../../../context/router/useRouter';
import useSchoolYears from '../../../hooks/useSchoolYears';
import {
  aggregateStatisticsByAgeGroup,
  getRegisterTotalJustificationCounts,
} from '../attendance-utils';
import { AttendanceStats } from '../AttendanceStatistics/AttendanceStats';
import { AttendanceRegisterPreviewGrid } from './AttendanceRegisterPreviewGrid';
import { canEditRegister, isRegisterDateEditable } from '../permissions';

interface AttendanceRegisterPreviewModalProps {}

export const AttendanceRegisterPreviewModal: FC<AttendanceRegisterPreviewModalProps> = () => {
  const { schoolId = '', permissions } = useAuth();
  const { formatMessage } = useIntl();
  const { data: codes } = useGetAttendanceCodesQuery(schoolId);
  const navigate = useNavigate();
  const { closeAndClean: closeModal } = useRouter();
  const { id } = useParams();
  const { defaultValidity } = useSchoolYears();

  const {
    data: register,
    isLoading: fetching,
    error,
  } = useGetAttendanceRegisterStatisticsQuery(id ?? '', schoolId, {
    enabled: Boolean(id && schoolId),
    refetchOnMount: 'always',
  });

  const canEdit =
    canEditRegister(permissions) &&
    !!register &&
    !!defaultValidity &&
    isRegisterDateEditable(register.date, defaultValidity);

  const ageGroups = useMemo(
    () => (register ? aggregateStatisticsByAgeGroup(register) : []),
    [register],
  );

  const { statistics, justificationCounts } = useMemo(() => {
    const statistics = ageGroups.flatMap((ageGroup) => ageGroup.attendanceDetails);
    const justificationCounts = getRegisterTotalJustificationCounts(
      ageGroups.map((ageGroup) => ageGroup.justificationCounts),
    );
    return { statistics, justificationCounts };
  }, [ageGroups]);

  const renderContent = () => {
    if (fetching) return <Loading />;

    if (error)
      return (
        <>
          <ModalHeader withBorderBottom={false} active>
            <IconButton onClick={closeModal}>
              <CrossIcon />
            </IconButton>
          </ModalHeader>
          <AccessDenied />
        </>
      );

    return (
      <>
        <ModalHeaderV2
          withGoBack
          active
          title={
            <Typography variant="h1">
              {format(newDateTimezoneOffset(register?.date), DATE_FORMAT_RANGE_FNS)}
            </Typography>
          }
        >
          {canEdit && (
            <IconButton onClick={() => navigate(`/attendance/${register?.id}/edit`)}>
              <EditIcon />
            </IconButton>
          )}

          <IconButton onClick={closeModal}>
            <CrossIcon />
          </IconButton>
        </ModalHeaderV2>
        <ModalMain>
          <CollapsiblePanel>
            <ModalSideBar
              sx={{
                paddingTop: (theme) => theme.spacing(1),
              }}
            >
              <MenuList>
                <MenuItem selected>
                  <FormattedMessage id="attendance-AmPmAttendance" />
                </MenuItem>
              </MenuList>
            </ModalSideBar>
          </CollapsiblePanel>

          <Stack p={3} width="100%" gap={1} overflow="hidden">
            <AttendanceStats
              statistics={[
                { counts: statistics, name: formatMessage({ id: 'attendance-Period' }) },
              ]}
              justificationCounts={justificationCounts}
              showChartBars={false}
              sx={(theme) => ({
                mb: theme.spacing(1.5),
                '& .MuiCard-root': { border: 'none', flex: 1 },
              })}
            />

            <AttendanceRegisterPreviewGrid codes={codes ?? []} ageGroups={ageGroups} />
          </Stack>
        </ModalMain>
      </>
    );
  };

  return (
    <ModalLarge open onClose={closeModal}>
      {renderContent()}
    </ModalLarge>
  );
};
